import React, { useEffect, useMemo, useRef, useState } from "react";

import { Link, useSearchParams } from "react-router-dom";

import { Search, CreditCard } from "lucide-react";

import { staffApi } from "@/lib/staffApi";

import { ErrorState, Skeleton } from "@/components/BookingShell";

import { formatMoney, paymentMethodLabel } from "@/lib/format";

import {paymentPeriod} from "@/lib/paymentPeriods";

const STATUS_STYLE = {

    paid:      "bg-emerald-500/15 border-emerald-400/40 text-emerald-100",

    pending:   "bg-amber-500/15 border-amber-400/40 text-amber-100",

    canceled:  "bg-red-500/10 border-red-400/30 text-red-200",

    partiallypaid: "bg-blue-500/10 border-blue-400/30 text-blue-100",

    refunded:  "bg-blue-500/10 border-blue-400/30 text-blue-100",

};

export function StaffPayments() {

    const [params, setParams] = useSearchParams();

    const [data, setData] = useState(null);

    const [error, setError] = useState(null);

    const requestVersion = useRef(0);

    const retryTimer = useRef(null);

    const [q, setQ] = useState("");

    const [status, setStatus] = useState(params.get("status") || "all");

    const [method, setMethod] = useState("all");

    const [start, setStart] = useState("");

    const [end, setEnd] = useState("");

    const [tab,setTab]=useState(params.get('status')==='outstanding'?'outstanding':params.get('status')==='deposit-outstanding'?'deposits':'received');

    const [period,setPeriod]=useState(params.get('status')?'all':'week'),[offset,setOffset]=useState(0),[page,setPage]=useState(1),[loading,setLoading]=useState(false);

    const range=period==='custom'?{start,end}:paymentPeriod(period,offset);

    const query={tab,start:range.start||undefined,end:range.end||undefined,page,limit:25,search:q,method,status};

    const queryKey=JSON.stringify(query);

    const load = () => {

        const version=++requestVersion.current;clearTimeout(retryTimer.current);setError(null);setLoading(true);

        const attempt=async(retry=0)=>{

            try {

                const result=await staffApi.payments(query);

                if(version!==requestVersion.current)return;

                setData(previous=>page>1&&previous ? {...result,payments:[...new Map([...previous.payments,...result.payments].map(p=>[p.id,p])).values()]} : result);

                setLoading(false);

                if(result.refreshing)retryTimer.current=setTimeout(()=>attempt(0),2500);

            } catch(e) {

                if(version!==requestVersion.current)return;

                if([502,503,504].includes(e.status)&&retry<(e.status===503?15:1))retryTimer.current=setTimeout(()=>attempt(retry+1),retry===0?750:2000);

                else {setError(e);setLoading(false);}

            }

        };

        attempt();

    };

    useEffect(()=>{

        load();

        return()=>{++requestVersion.current;clearTimeout(retryTimer.current);};

        // eslint-disable-next-line react-hooks/exhaustive-deps

    },[queryKey]);

    const chooseTab=next=>{setTab(next);setStatus(next==='outstanding'?'outstanding':next==='deposits'?'deposit-outstanding':'all');setPeriod(next==='received'?'week':'all');setOffset(0);setPage(1);setData(null);};

    // Sync status filter -> URL for deep-links

    useEffect(() => {

        const next = new URLSearchParams(params);

        if (status !== "all") next.set("status", status); else next.delete("status");

        setParams(next, { replace: true });

        // eslint-disable-next-line

    }, [status]);

    const payments = data?.payments || [];

    // Amelia's actual gateway list — never invented.

    const methodOptions = useMemo(() => {

        const set = new Set(data?.methods || []);

        payments.forEach((p) => { if (p.gateway) set.add(p.gateway); });

        return ["all", ...Array.from(set).sort()];

    }, [payments, data?.methods]);

    // Real statuses Amelia sends

    const statusOptions = useMemo(() => {

        const set = new Set();

        payments.forEach((p) => { if (p.status) set.add(p.status); });

        return ["all", "deposit-outstanding", "outstanding", ...Array.from(set).sort()];

    }, [payments]);

    // The server filters the complete verified report before pagination.
    // Filtering again by appointment date would hide receipts paid this week
    // for a future appointment.
    const filtered = payments.filter(p=>status==='deposit-outstanding'?p.depositDue===true:status==='outstanding'?p.balanceOutstanding===true:status==='all'||String(p.status||'').toLowerCase()===status.toLowerCase());

    const totals = useMemo(() => {

        const paid = filtered.reduce((s, p) => s + Number(p.amount || 0), 0);

        const outstanding = filtered.reduce((s, p) => s + Number((status === 'deposit-outstanding' ? p.depositOutstanding : p.outstandingBalance) || 0), 0);

        return data?.totals || { paid, outstanding, count: filtered.length };

    }, [filtered, status, data?.totals]);

    return (

        <div className="space-y-6" data-testid="staff-payments">

            <header>

                <h1 className="font-serif font-light text-4xl text-white">Payments</h1>

                <p className="text-sm text-white/50 mt-1">

                    View payments, deposits and outstanding balances in one place.

                </p>

            <button type="button" onClick={load} disabled={!data && !error} className="text-gold mt-2 disabled:opacity-50">{!data && !error ? 'Loading payments…' : 'Refresh'}</button>

            </header>

            <div role="tablist" aria-label="Payment views" className="flex flex-wrap gap-2">{[['received','Payments received'],['outstanding','Outstanding balances'],['deposits','Deposits due']].map(([key,label])=><button type="button" role="tab" aria-selected={tab===key} key={key} onClick={()=>chooseTab(key)} className={`rounded-full px-4 py-2 text-sm ${tab===key?'bg-gold text-navy':'border hairline text-navy'}`}>{label}</button>)}</div>

            <div className="flex flex-wrap items-center gap-2 text-navy"><label>Period <select aria-label="Payment period" value={period} onChange={e=>{setPeriod(e.target.value);setOffset(0);setPage(1);}} className="rounded-xl border border-gold/40 bg-white p-2">{[['week','Week'],['month','Month'],['quarter','Quarter'],['year','Year'],['all','All dates'],['custom','Custom dates']].map(([key,label])=><option key={key} value={key}>{label}</option>)}</select></label>

                {!['all','custom'].includes(period)&&<><button type="button" aria-label="Previous period" onClick={()=>{setOffset(n=>n-1);setPage(1);}} className="rounded-full border border-gold/40 px-3 py-2">Previous</button><button type="button" aria-label="Next period" onClick={()=>{setOffset(n=>n+1);setPage(1);}} className="rounded-full border border-gold/40 px-3 py-2">Next</button><span className="text-sm">{range.start} – {range.end}</span></>}

            </div>

            {data?.verified_at&&<p className="text-sm text-navy/70" role="status">Last verified {new Date(data.verified_at).toLocaleString('en-GB')}{data.refreshing?' · Updating in the background…':''}</p>}

            {data?.undated_payments>0&&<p className="text-sm text-amber-800">{data.undated_payments} payment records have no verified payment date and are excluded from this date range. Choose All dates to view them.</p>}

            {error && <ErrorState title="We couldn't load payments." label="Payments unavailable" error={error} onRetry={load} />}

            {!data && !error && <Skeleton className="h-64" />}

            {data && (

                <>

                    {/* Totals for the current filter */}

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="payment-totals">

                        <div className="surface-highlight rounded-2xl p-4">

                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/85 font-semibold">Paid (filtered)</div>

                            <div className="mt-2 font-serif text-2xl text-gold">{formatMoney(totals.paid)}</div>
                            {tab==='received'&&<p className="text-xs text-white/70 mt-2">New income: {formatMoney(totals.income??totals.paid)}. Voucher redemptions and customer credit are excluded from new income.</p>}

                        </div>

                        <div className="surface-highlight rounded-2xl p-4">

                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/85 font-semibold">{status === 'deposit-outstanding' ? 'Deposits due' : 'Outstanding (filtered)'}</div>

                            <div className="mt-2 font-serif text-2xl text-gold">{formatMoney(totals.outstanding)}</div>

                        </div>

                        <div className="surface-highlight rounded-2xl p-4">

                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/85 font-semibold">Records</div>

                            <div className="mt-2 font-serif text-2xl text-gold">{totals.count}</div>

                        </div>

                    </div>

                    {/* Filters */}

                    <div className="flex flex-wrap gap-2" data-testid="payment-filters">

                        <div className="relative">

                            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />

                            <input value={q} onChange={(e) => {setQ(e.target.value);setPage(1);}} data-testid="payments-search"

                                   placeholder="Search by customer, treatment or booking"

                                   className="pl-9 pr-3 h-9 w-72 max-w-full rounded-full bg-transparent border hairline text-sm text-white placeholder-white/40 focus:border-gold outline-none" />

                        </div>

                        <select value={status} onChange={(e) => {const v=e.target.value;setStatus(v);setPage(1);if(v==='outstanding'){setTab('outstanding');setPeriod('all');}else if(v==='deposit-outstanding'){setTab('deposits');setPeriod('all');}}} data-testid="filter-status"

                                className="h-9 rounded-full bg-transparent border hairline text-xs text-white/80 px-3">

                            {statusOptions.map((s) => <option key={s} value={s} className="bg-navy">{s === "all" ? "All statuses" : s === "deposit-outstanding" ? "Outstanding deposits" : s === "outstanding" ? "Outstanding payments" : s === "partiallyPaid" ? "Part paid" : s}</option>)}

                        </select>

                        <select value={method} onChange={(e) => {setMethod(e.target.value);setPage(1);}} data-testid="filter-method"

                                className="h-9 rounded-full bg-transparent border hairline text-xs text-white/80 px-3">

                            {methodOptions.map((m) => <option key={m} value={m} className="bg-navy">{m === "all" ? "All methods" : paymentMethodLabel(m, "paid")}</option>)}

                        </select>

                        <input type="date" value={range.start} onChange={(e) => {setStart(e.target.value);setEnd(range.end);setPeriod('custom');setPage(1);}} data-testid="filter-start" aria-label="From date"

                               className="h-9 rounded-full bg-transparent border hairline text-xs text-white/80 px-3" />

                        <input type="date" value={range.end} onChange={(e) => {setEnd(e.target.value);setStart(range.start);setPeriod('custom');setPage(1);}} data-testid="filter-end" aria-label="To date"

                               className="h-9 rounded-full bg-transparent border hairline text-xs text-white/80 px-3" />

                    </div>

                    {data.has_more&&<button type="button" disabled={loading} onClick={()=>setPage(n=>n+1)} className="rounded-full bg-gold text-navy px-5 py-3">{loading?'Loading…':'Load more'}</button>}

                    {filtered.length === 0 ? (

                        <div className="rounded-2xl glass p-8 text-center text-white/60" data-testid="payments-empty">

                            {error ? 'Current balances could not be verified. Refresh to check again.' : q || method !== 'all' || start || end ? 'No payment records match these filters.' : status === 'outstanding' ? 'No outstanding payments.' : status === 'deposit-outstanding' ? 'No outstanding deposits.' : payments.length === 0 ? 'No payment records yet.' : 'No payment records match these filters.'}

                        </div>

                    ) : (

                        <ul className="space-y-2" data-testid="payment-list">

                            {filtered.map((p) => {

                                const status = (p.status || "").toLowerCase();

                                const outstanding = Number(p.outstandingBalance || 0);

                                const paidClass = STATUS_STYLE[status] || "bg-white/10 border-white/25 text-white/80";

                                return (

                                    <li key={p.id} className="payment-card surface-highlight rounded-2xl border hairline p-4 hover:border-gold/50" data-testid={`payment-${p.id}`}>

                                        <Link to={p.appointmentId ? `/staff/appointments/${p.appointmentId}` : "#"} className="payment-card-layout">

                                            <div className="h-10 w-10 rounded-full glass flex items-center justify-center shrink-0">

                                                <CreditCard className="h-4 w-4 text-gold" strokeWidth={1.5} />

                                            </div>

                                            <div className="min-w-0 flex-1">

                                                <div className="text-white font-ui text-base break-words">{p.customerName || "—"}</div>

                                                <div className="text-sm text-white/75 break-words">{p.treatment || "—"}</div>

                                                <div className="text-xs text-white/70 flex flex-wrap gap-2 mt-2">

                                                    {(tab==='received'?p.dateTime:p.appointmentDate) && <span>{formatUKDateTime(tab==='received'?p.dateTime:p.appointmentDate)}</span>}

                                                    {p.employeeId && <><span>·</span><span className="normal-case tracking-normal">Employee #{p.employeeId}</span></>}

                                                </div>

                                            </div>

                                            <div className="payment-card-amounts">

                                                <div className="text-white font-ui">{formatMoney(p.amount || 0)} <span className="text-white/40">paid</span></div>

                                                <div className={`text-[11px] ${outstanding > 0 ? "text-amber-200" : "text-emerald-200"}`}>

                                                    {formatMoney(outstanding)} balance owing

                                                    <div>{p.depositOutstanding == null ? 'Deposit settings unavailable' : `${formatMoney(p.depositOutstanding)} deposit due`}</div>

                                                </div>

                                            </div>

                                            <span className="text-[10px] uppercase tracking-[0.18em] text-white/70" data-testid={`payment-method-${p.id}`}>

                                                {p.gatewayTitle || paymentMethodLabel(p.gateway, p.status)}

                                            </span>

                                            <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${paidClass}`} data-testid={`payment-status-${p.id}`}>

                                                {status === "partiallypaid" ? "Part paid" : p.status || "Unknown"}

                                            </span>

                                        </Link>

                                    </li>

                                );

                            })}

                        </ul>

                    )}

                </>

            )}

        </div>

    );

}

function formatUKDateTime(iso) {

    if (!iso) return "";

    try { return new Date(iso.replace(" ", "T")).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }

    catch { return iso; }

}

